import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { PulseDot } from '../ui';
import DealCard, { Prospect, Stage } from './DealCard';
import { shortMoney } from './meta';

interface StageColumnProps {
  stage: Stage;
  prospects: Prospect[];
  onEdit: (prospect: Prospect) => void;
  onDelete: (id: string) => void;
  onAdvance: (prospect: Prospect) => void;
}

export default function StageColumn({ stage, prospects, onEdit, onDelete, onAdvance }: StageColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `droppable-${stage.id}`,
  });

  const totalValue = prospects.reduce((sum, prospect) => sum + prospect.deal_value, 0);
  const isWon = stage.id === 'closed_won';
  const isLost = stage.id === 'closed_lost';

  return (
    // Phones: one column fills most of the screen with the next one peeking in.
    <div
      ref={setNodeRef}
      className="snap-start w-[min(320px,calc(100vw-64px))] md:w-[252px]"
      style={{
        flexShrink: 0,
        background: 'var(--surface-card)',
        border: `1px solid ${isOver ? 'var(--accent)' : 'var(--border-hairline)'}`,
        borderRadius: 'var(--radius-lg)',
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        minHeight: '320px',
        transition: 'border-color var(--dur-base) var(--ease-out)',
      }}
    >
      {/* Column header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: '8px',
          padding: '2px 2px 8px',
          borderBottom: '1px solid var(--border-hairline)',
        }}
      >
        {isWon ? <PulseDot tone="up" pulse={false} /> : null}
        {isLost ? <PulseDot tone="down" pulse={false} /> : null}
        <span className="t-eyebrow">{stage.name}</span>
        <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
          {prospects.length}
        </span>
        <span
          className="t-num"
          style={{
            marginLeft: 'auto',
            fontSize: 'var(--text-xs)',
            color: totalValue > 0 ? 'var(--text-muted)' : 'var(--text-faint)',
          }}
        >
          {totalValue > 0 ? shortMoney(totalValue) : '—'}
        </span>
      </div>

      <SortableContext items={prospects.map((prospect) => prospect.id)} strategy={verticalListSortingStrategy}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
          {prospects.map((prospect) => (
            <DealCard
              key={prospect.id}
              prospect={prospect}
              onEdit={onEdit}
              onDelete={onDelete}
              onAdvance={onAdvance}
            />
          ))}

          {prospects.length === 0 && (
            <div
              style={{
                border: '1px dashed var(--border-hairline)',
                borderRadius: 'var(--radius-md)',
                padding: '14px',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-faint)',
                textAlign: 'center',
              }}
            >
              Drop deals here
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
}
